import React, { useState } from 'react';
import CtaButton from '@/components/CtaButton';
import { requestManageLink } from '@/services/api';

/**
 * Self-serve recovery for tokenless or dead-link visits — emailed links
 * expire (60-day TTL, revoked when a newer one is issued), so parents
 * opening a stale email or bookmark request a fresh secure link here.
 * The confirmation copy is identical whether or not the email matched an
 * account (the API never reveals that either).
 */
const RequestLinkScreen = ({ onBack }) => {
	const [email, setEmail] = useState('');
	const [busy, setBusy] = useState(false);
	const [sent, setSent] = useState(false);

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (busy || !email.trim()) return;
		setBusy(true);
		try {
			await requestManageLink(email.trim());
		} catch {
			// Constant outcome by design — throttled/failed requests read the
			// same as success so the form can't be used to probe accounts.
		}
		setSent(true);
		setBusy(false);
	};

	if (sent) {
		return (
			<div className="flex flex-col items-center gap-[18px] pt-[12px] text-center">
				<div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-pay-summary text-[32px]">
					✉️
				</div>
				<h1 className="text-[24px] font-extrabold text-pay-ink">Check your email</h1>
				<p className="text-[14px] leading-[21px] text-pay-muted">
					If {email.trim()} matches a payer account, a secure Auto-Pay link is on its way. It can take a minute or two —
					check your spam folder if you don’t see it.
				</p>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit} className="flex flex-col gap-[20px]">
			<h1 className="text-[24px] font-extrabold text-pay-ink">Get a secure link</h1>

			<p className="text-[14px] leading-[21px] text-pay-muted">
				Enter the email address your school has on file for billing. We’ll send you a fresh secure link to manage
				Auto-Pay — you’ll confirm your student’s last name before anything is shown.
			</p>

			<div className="flex w-full flex-col gap-[7px]">
				<label className="text-[13px] font-semibold text-pay-ink" htmlFor="pay-request-email">
					Email
				</label>
				<input
					id="pay-request-email"
					type="email"
					value={email}
					onChange={(event) => setEmail(event.target.value)}
					placeholder="you@example.com"
					autoComplete="email"
					className="h-[50px] w-full rounded-[12px] border-[1.5px] border-pay-line bg-white px-[16px] text-[15px] text-pay-ink outline-none placeholder:text-pay-muted focus:border-pay-purple"
				/>
			</div>

			<div className="h-[4px]" />

			<CtaButton type="submit" disabled={busy || !email.trim()}>
				{busy ? 'Sending…' : 'Email me a secure link'}
			</CtaButton>

			{onBack ? (
				<button
					type="button"
					onClick={onBack}
					className="cursor-pointer border-0 bg-transparent text-[14px] font-semibold text-pay-purple"
				>
					Back
				</button>
			) : null}
		</form>
	);
};

export default RequestLinkScreen;
